import { messagesNow } from '@/i18n'
import { formatCredits, formatCreditsWithWord } from '@/features/ai/lib/money'
import { messages } from './messages'

export interface PaymentStatusView {
  label: string
  tone: 'positive' | 'negative' | 'neutral'
}

/**
 * Статусы заказа. Неоплаченный и отклонённый из списка не пропадают намеренно:
 * иначе «оплатил, не зачислилось» становится неразрешимым вопросом поддержки.
 *
 * `Map`, а не литерал, и незнакомое значение — нейтральное: угаданный красный
 * над непонятным статусом пугает сильнее, чем сырая строка.
 */
type StatusView = Omit<PaymentStatusView, 'label'> & {
  label: (m: (typeof messages)['ru']) => string
}

const STATUS = new Map<string, StatusView>([
  ['initialized', { label: (m) => m.statusInitialized, tone: 'neutral' }],
  ['completed', { label: (m) => m.statusCompleted, tone: 'positive' }],
  ['canceled', { label: (m) => m.statusCanceled, tone: 'neutral' }],
  ['failed', { label: (m) => m.statusFailed, tone: 'negative' }],
  ['refunded', { label: (m) => m.statusRefunded, tone: 'neutral' }],
  ['gifted', { label: (m) => m.statusGifted, tone: 'positive' }],
])

export const paymentStatusLabel = (status: string | null | undefined): PaymentStatusView => {
  const known = STATUS.get((status ?? '').trim().toLowerCase())
  return known
    ? { label: known.label(messagesNow(messages)), tone: known.tone }
    : { label: status || '—', tone: 'neutral' }
}

/**
 * Сумма заказа. Способа оплаты в ПОЛЬЗОВАТЕЛЬСКОМ контракте нет — поле
 * `currency` отдаёт только админская ручка, — поэтому печатаем валюту, которой
 * заплатили, и ничего не обещаем про карту или кошелёк.
 */
/** «190.00000000» → «190», «2.5000» → «2.50»: бэк отдаёт цену с восемью знаками. По
 *  строке, без `Number`, — деньги к числу не приводим. */
const tidyAmount = (value: string): string => {
  const match = /^(-?\d+)(?:\.(\d+))?$/.exec(value.trim())
  if (!match) return value
  const frac = (match[2] ?? '').replace(/0+$/, '')
  return frac ? `${match[1]}.${frac.padEnd(2, '0')}` : match[1]
}

const CURRENCY = new Map<string, (value: string) => string>([
  ['RUB', (value) => `${value} ₽`],
  ['USD', (value) => `$${value}`],
  ['XTR', (value) => `${value} ★`],
])

export const paymentAmountText = (
  finalPrice: string | null | undefined,
  currency: string | null | undefined,
): string => {
  if (!finalPrice) return '—'
  const known = currency ? CURRENCY.get(currency) : undefined
  // Незнакомая валюта печатается рядом с числом, отсутствующая — не печатается
  // вовсе: «190.00 null» хуже, чем «190.00».
  const amount = tidyAmount(finalPrice)
  return known ? known(amount) : `${amount}${currency ? ` ${currency}` : ''}`
}

/** «200.0000» → «200», «12.5000» → «12.5»: число кредитов для показа, по строке. */
export const creditsAmount = (credits: string): string => formatCredits(credits)

/**
 * Подпись пакета на языке интерфейса. Число кредитов приходит в `product.credits`
 * (строка, только у `ai_credits`) — по нему и печатаем, имя товара не нужно. При
 * `null` (старые заказы, чужой тип) число берём из начала имени — готовой русской
 * строки «200 кредитов»; не нашли — показываем имя как есть.
 */
export const paymentPackageText = (
  name: string | undefined,
  fallback: string,
  credits?: string | null,
): string => {
  if (credits) return formatCreditsWithWord(credits)
  if (!name) return fallback
  const lead = /^\s*(\d[\d\s]*)/.exec(name)
  return lead ? formatCreditsWithWord(lead[1].replace(/\s/g, '')) : name
}
